import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { PlatformService } from './platform.service'
import { CookieService } from './cookie.service'

export interface ViewedUserInfo {
  email: string
  id: number
  name?: string
}

@Injectable({
  providedIn: 'root'
})
export class ViewAsService {
  private readonly ORIGINAL_USER_STORAGE_KEY = 'flo_original_user_tokens'
  private readonly VIEWED_USER_INFO_KEY = 'flo_viewed_user_info'

  private readonly _isViewingAsUser$ = new BehaviorSubject<boolean>(false)
  private readonly _viewedUserInfo$ = new BehaviorSubject<ViewedUserInfo | null>(null)

  readonly isViewingAsUser$ = this._isViewingAsUser$.asObservable()
  readonly viewedUserInfo$ = this._viewedUserInfo$.asObservable()

  constructor(private platformService: PlatformService, private cookieService: CookieService) {
    // Initialize state from cookies on service creation (only on browser)
    this.initializeState()
  }

  private initializeState() {
    if (!this.platformService.isBrowser) {
      // On server, initialize with default values
      this._isViewingAsUser$.next(false)
      this._viewedUserInfo$.next(null)
      return
    }

    const isViewingAs = !!this.getOriginalUserTokens()
    const viewedUserInfo = this.getViewedUserInfo()

    this._isViewingAsUser$.next(isViewingAs)
    this._viewedUserInfo$.next(viewedUserInfo)
  }

  setViewingAsUser(userInfo: ViewedUserInfo) {
    this.saveViewedUserInfo(userInfo)
    this._isViewingAsUser$.next(true)
    this._viewedUserInfo$.next(userInfo)
  }

  clearViewingAsUser() {
    this.clearViewedUserInfo()
    this.clearOriginalUserTokens()
    this._isViewingAsUser$.next(false)
    this._viewedUserInfo$.next(null)
  }

  private saveViewedUserInfo(userInfo: ViewedUserInfo) {
    if (this.platformService.isBrowser) {
      localStorage.setItem(this.VIEWED_USER_INFO_KEY, JSON.stringify(userInfo))
    }
  }

  private getViewedUserInfo(): ViewedUserInfo | null {
    if (!this.platformService.isBrowser) {
      return null
    }
    const stored = localStorage.getItem(this.VIEWED_USER_INFO_KEY)
    return stored ? JSON.parse(stored) : null
  }

  private clearViewedUserInfo() {
    if (this.platformService.isBrowser) {
      localStorage.removeItem(this.VIEWED_USER_INFO_KEY)
    }
  }

  private getOriginalUserTokens(): any {
    if (!this.platformService.isBrowser) {
      return null
    }
    const stored = this.cookieService.get(this.ORIGINAL_USER_STORAGE_KEY)
    return stored ? (typeof stored === 'string' ? JSON.parse(stored) : stored) : null
  }

  private clearOriginalUserTokens() {
    if (this.platformService.isBrowser) {
      this.cookieService.remove(this.ORIGINAL_USER_STORAGE_KEY)
    }
  }
}
